import { Outlet, useLocation } from 'react-router-dom'
import { Box } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'

import { RootState } from '@store'
import { SHOW_SIDEBAR } from '@store/actions'
import { Header } from '@components/Layout'
import Breadcrumbs from '@components/Layout/Breadcrumbs/Breadcrumbs'
import AccountDropdown from '@components/Layout/Header/AccountDropdown'
import OrgPicker from '@components/Layout/Header/OrgPicker'
import { account, organizationSettings } from '@constants/routing'
import useOrganizationSettingsAccess from '@/hooks/useOrganizationSettingsAccess'
import { usePageTracking } from '@/hooks/usePageTracking'
import AssistantChat, { GlobalAssistantOpenButton } from '@components/AssistantChat'
import { useEffect, useState } from 'react'
import { useAuth, track, eventTypes } from 'torqcloud-ui'
import { useModelDataSecurity } from '@/contexts/ModelDataSecurityContext'
import { appConfig } from '@/config'

import DashboardSidebar from '../layouts/DashboardSidebar'
import ErrorBoundary from '../ErrorBoundary'

export default function AppLayout() {
  const dispatch = useDispatch()
  const { pathname } = useLocation()
  const sidebar = useSelector((state: RootState) => state.sidebar)
  const { confidentiality } = useModelDataSecurity()
  const { canAccessOrganizationSettings } = useOrganizationSettingsAccess()
  const [assistantOpen, setAssistantOpen] = useState(false)
  const { permissions } = useAuth()
  const canAccessAssistant = permissions?.canAccessWorkforceAssistant ?? false

  // Emit a Segment page view on every route change from a single place.
  usePageTracking()

  useEffect(() => {
    if (!canAccessAssistant && assistantOpen) {
      setAssistantOpen(false)
    }
  }, [assistantOpen, canAccessAssistant])

  const setIsSidebarOpen = (isOpen: boolean) => {
    sessionStorage.setItem('sidebarOpen', JSON.stringify(isOpen))
    dispatch({ type: SHOW_SIDEBAR, show: isOpen })
  }

  useEffect(() => {
    const content = document.getElementById('app__content')
    if (!content) return

    const setCrispContentAnchor = () => {
      const rect = content.getBoundingClientRect()
      const rightOffset = Math.max(24, window.innerWidth - rect.right + 12)
      const crispChatbox = document.getElementById('crisp-chatbox')
      const crispLauncher = crispChatbox?.querySelector<HTMLElement>('[aria-label="Open chat"]')
      const crispWindow = crispChatbox?.querySelector<HTMLElement>('[aria-label="Chat window"]')?.parentElement
      const crispPositionedElements = [crispLauncher, crispWindow]

      document.documentElement.style.setProperty('--app-content-right-offset', `${rightOffset}px`)

      if (!crispChatbox || crispChatbox.dataset.fullView === 'true' || crispChatbox.dataset.positionReverse === 'true') {
        crispPositionedElements.forEach(element => element?.style.removeProperty('right'))
        return
      }

      crispPositionedElements.forEach(element => {
        element?.style.setProperty('right', `${rightOffset}px`, 'important')
      })
    }

    const setCrispContentAnchorAfterLayout = () => {
      requestAnimationFrame(setCrispContentAnchor)
    }

    setCrispContentAnchorAfterLayout()

    const observer = new ResizeObserver(setCrispContentAnchorAfterLayout)
    observer.observe(content)

    const mutationObserver = new MutationObserver(setCrispContentAnchorAfterLayout)
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    })

    window.addEventListener('resize', setCrispContentAnchorAfterLayout)

    return () => {
      observer.disconnect()
      mutationObserver.disconnect()
      window.removeEventListener('resize', setCrispContentAnchorAfterLayout)
      document.documentElement.style.removeProperty('--app-content-right-offset')
    }
  }, [assistantOpen])

  return (
    <Box id="app__root" sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Header confidentiality={confidentiality} environmentMarking={appConfig.environmentMarking} />
      <Box
        id="app__body"
        sx={{
          display: 'flex',
          flex: '1 1 auto',
          overflow: 'hidden',
          gap: { xs: 0, md: '8px' },
          backgroundColor: t => t.palette.background.default,
        }}
      >
        <Box component="aside" id="app__sidebar" sx={{ display: 'flex', flexShrink: 0, pl: { md: '1rem' } }}>
          <DashboardSidebar openMobile={sidebar.show} onMobileClose={isOpen => setIsSidebarOpen(isOpen ?? !sidebar.show)} />
        </Box>
        <Box
          id="app__content"
          sx={{ position: 'relative', display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, overflow: 'hidden' }}
        >
          <Box
            id="app__topbar"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem',
              px: 2,
              py: 1,
              flexShrink: 0,
            }}
          >
            <Breadcrumbs />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {canAccessAssistant && (
                <GlobalAssistantOpenButton
                  onClick={() => {
                    track(eventTypes.GLOBAL_ASSISTANT_OPENED)
                    setAssistantOpen(true)
                  }}
                />
              )}
              <OrgPicker />
              <AccountDropdown
                userSettingsUrl={account()}
                organizationSettingsUrl={organizationSettings()}
                showOrganizationSettings={canAccessOrganizationSettings}
              />
            </Box>
          </Box>

          <Box component="main" id="app__main" sx={{ flex: 1, overflow: 'auto', pl: 2, pr: { xs: 2, md: 'calc(16px + 1rem)' } }}>
            {/* Route-level boundary: a crash in the current page shows a recoverable
                inline fallback while the header/sidebar stay mounted, and resets
                automatically when the user navigates to another route. */}
            <ErrorBoundary boundaryName="route" variant="inline" resetKeys={[pathname]}>
              <Outlet />
            </ErrorBoundary>
          </Box>
        </Box>
        {canAccessAssistant && <AssistantChat open={assistantOpen} onClose={() => setAssistantOpen(false)} />}
      </Box>
    </Box>
  )
}
